import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import {
  getProductInfo,
  getProductGroupInfo,
  getProductByProductGroup,
} from "../services/ProductService";
import { CustomerContext } from "../context/customerContext";

function getScrollParent(node) {
  if (!node) return window;
  let el = node.parentElement;
  while (el && el !== document.body) {
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    const canScroll =
      /(auto|scroll|overlay)/.test(overflowY) &&
      el.scrollHeight > el.clientHeight;
    if (canScroll) return el;
    el = el.parentElement;
  }
  return window;
}

function getOffsetTopRelativeTo(el, ancestor) {
  if (!el) return 0;
  if (ancestor === window) {
    const rect = el.getBoundingClientRect();
    return rect.top + window.scrollY;
  }
  // جمع offsetTop تا رسیدن به ancestor
  let top = 0;
  let node = el;
  while (node && node !== ancestor && node instanceof HTMLElement) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return top;
}

const ProductCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, setCustomer } = useContext(CustomerContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // Sticky/fixed header
  const headerWrapRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollElRef = useRef(null);
  const thresholdRef = useRef(0);

  const handleCategoryChange = async (e) => {
    const selectedId = e.target.value;
    setSelectedCategory(selectedId);

    if (selectedId === "all") {
      const res = await getProductInfo();
      setProducts(res.data);
    } else {
      try {
        const res = await getProductByProductGroup(selectedId);
        setProducts(res.data);
      } catch (error) {
        console.error("خطا در دریافت محصولات این دسته:", error);
      }
    }
  };

  // 1) Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, categoryRes] = await Promise.all([
          getProductInfo(),
          getProductGroupInfo(),
        ]);

        setProducts(productRes.data);
        setCategories([{ id: "all", name: "همه" }, ...categoryRes.data]);
      } catch (error) {
        console.error("خطا در دریافت اطلاعات محصولات یا دسته‌بندی‌ها:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 2) Setup scroll handling (works for window or inner scroll containers)
  useEffect(() => {
    if (!headerWrapRef.current) return;

    // پیدا کردن اسکرول‌Parent
    const scrollEl = getScrollParent(headerWrapRef.current);
    scrollElRef.current = scrollEl;

    // اندازه‌گیری اولیه
    const measure = () => {
      if (!headerWrapRef.current) return;
      const h = headerWrapRef.current.offsetHeight || 0;
      setHeaderHeight(h);
      thresholdRef.current = getOffsetTopRelativeTo(
        headerWrapRef.current,
        scrollEl
      );
    };

    const onScroll = () => {
      const scrollTop =
        scrollEl === window ? window.scrollY : scrollEl.scrollTop;
      setStuck(scrollTop >= thresholdRef.current);
    };

    // اندازه‌گیری‌های اولیه و لیسنرها
    measure();
    onScroll();

    window.addEventListener("resize", measure);
    if (scrollEl === window) {
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    }

    // Cleanup
    return () => {
      window.removeEventListener("resize", measure);
      if (scrollEl === window) {
        window.removeEventListener("scroll", onScroll);
      } else {
        scrollEl.removeEventListener("scroll", onScroll);
      }
    };
  }, [loading, categories.length]); // بعد از لود و تغییر طول هدر دوباره اندازه بگیرد


  

  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      selectedCategory === "all" ||
      product.productGroupId?.toString() === selectedCategory.toString();

    const matchesSearch = product.productName
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });


  
  useEffect(() => {
    if (!sentinelRef.current) return;
  
    // ریشهٔ اسکرول: اگر window باشد null بده
    const rootEl = scrollElRef.current === window ? null : scrollElRef.current;
  
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;
  
        // اگر هنوز چیز برای نمایش باقی مانده و الان در حال لود نیستیم
        if (visibleCount < filteredProducts.length && !loadingMore) {
          setLoadingMore(true);
  
          // اگر در آینده از API صفحه‌به‌صفحه می‌آوری، اینجا call کن
          // فعلاً نمایش مرحله‌ای کلاینتی:
          setTimeout(() => {
            setVisibleCount((prev) =>
              Math.min(prev + 12, filteredProducts.length)
            );
            setLoadingMore(false);
          }, 500);
        }
      },
      {
        root: rootEl ?? null,    // null یعنی window
        rootMargin: "200px 0px", // زودتر تریگر شود
        threshold: 0,
      }
    );
  
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [
    filteredProducts.length, // وقتی تعداد نتایج تغییر کرد، اُبزرور را بازتنظیم کن
    visibleCount,
    loadingMore,
  ]);

  if (loading || !products) {
    return (
      <DashboardLayout>
        <div className="text-center text-gray-500 mt-10" dir="rtl">
          در حال بارگذاری...
        </div>
      </DashboardLayout>
    );
  }

  const containerCls = "mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8";

  return (
    <DashboardLayout>
      {/* هدر */}
      <div
        className={
          stuck
            ? "fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur shadow-md"
            : ""
        }
      >
        <div className={containerCls} ref={headerWrapRef}>
          <div className="rounded-lg border  overflow-hidden">
            {/* ردیف عنوان */}
            <div
              className="flex items-center justify-between  py-2 border-b border-gray-200"
              dir="rtl"
            >
              <h1 className="text-base sm:text-lg md:text-xl font-bold text-blue-700">
                کاتالوگ محصولات
              </h1>
              <button
                onClick={() => navigate(-1)}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
              >
                بازگشت
              </button>
            </div>

            {/* فیلترها */}
            <div className="px-6 py-2 bg-gray-50" dir="rtl">
              <label
                className="text-sm font-medium text-gray-700 block mb-1"
                htmlFor="categorySelect"
              >
                انتخاب دسته‌بندی
              </label>

              <div className="flex flex-row items-center gap-2">
                <select
                  id="categorySelect"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-1/3 sm:w-40 md:w-48 lg:w-56 focus:outline-none"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="جستجو..."
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-2/3 sm:w-64 md:w-72 lg:w-80 focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* فاصله در حالت fixed */}
      {stuck && <div style={{ height: headerHeight }} />}

      {/* لیست محصولات */}
      <div className={containerCls}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-xs sm:text-sm md:text-sm mt-3">
          {filteredProducts.slice(0, visibleCount).map((product) => {
            const imagePath = `/Product-Image/${product.productCode}.jpg`;
            return (
              <div
                key={product.productCode}
                className="border rounded p-2 shadow mx-auto w-full max-w-[300px]"
              >
                <img
                  src={imagePath}
                  alt={product.productName}
                  className="w-full h-auto object-cover rounded transition-transform duration-300 ease-in-out hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/Product-Image/default.jpg";
                  }}
                />
                <h3 className="mt-2 text-center font-semibold text-[10px] sm:text-sm md:text-sm">
                  {product.productName}
                </h3>
              </div>
            );
          })}
        </div>

        {visibleCount < filteredProducts.length && <div ref={sentinelRef} />}

        {/* اسپینر پایین لیست وقتی در حال لود هستیم */}
        {loadingMore && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductCatalog;
